import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  faBan, faCheckCircle,
} from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useOrderBundles from '@hooks/useOrderBundles'
import { generateUrls } from '@hooks/useOrderBundle'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import OrderBundleListItem from '../OrderBundleListItem'

// @ts-expect-error TS7006
const buildPageFilterFields = (shouldRenderPublicationFilter) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: {
        shouldRender: () => shouldRenderPublicationFilter,
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
      },
      key: 'publication_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'provider_price_lists',
        entityKey: 'providerPriceLists',
      },
      key: 'provider_price_list_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'min_price',
      label: 'Filter by Min Price',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'max_price',
      label: 'Filter by Max Price',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'global',
      label: 'Global Status',
      values: [
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

type QueryParams = { hidden?: 'true' | 'false'}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden }: QueryParams = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden || 'false'

  const orderBundlesPayload = useOrderBundles(
    {
      filters: { hidden: isHidden, ...pageFilters },
      performHttpRequests: true,
      requestOptions: {
        include_order_bundle_price_list_ids: !!pageFilters.provider_price_list_id,
        include_order_bundle_publication_ids: !!pageFilters.publication_id,
      },
    },

  )
  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderBundlesCount,
    filteredOrderBundles,
    hasFilteredOrderBundles,
    loading,
  } = orderBundlesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateOrderBundleModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { orderBundlesIndexUrl, hiddenOrderBundlesIndexUrl } = generateUrls()

  const { currentUser, isProviderUser } = useCurrentUser()
  // @ts-expect-error TS2339
  const { association } = currentUser
  const { is_publication_provider } = association || {}

  const shouldRenderPublicationFilter = isProviderUser ? is_publication_provider : true

  return (
    <>
      <Helmet>
        <title>Bundles | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateOrderBundleModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        tabBarItems={[
          {
            href: orderBundlesIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current Bundles',
          },
          {
            href: hiddenOrderBundlesIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Archived Bundles',
          },
        ]}
        title="Order Bundles"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredOrderBundles && <ListBlankState />}

            {!loading && hasFilteredOrderBundles && (
              <DashboardModule title="Bundles">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredOrderBundles.map(orderBundle => (
                      <OrderBundleListItem key={orderBundle.id} orderBundle={orderBundle} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredOrderBundlesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(shouldRenderPublicationFilter)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
